/**
 * Shared file-size limits enforced across every file-record write surface
 * (chat attachments, workspace uploads, and any future file-record write
 * path). The canonical values live here so the clientside floor matches
 * the server's S3 presign policy (`content-length-range: 0 .. 100 MB` in
 * `file_record_manager_object.go::PresignPostObject`) — if they drift, the
 * user sees their upload succeed for hundreds of megabytes only to 403 at
 * the S3 edge with a generic "policy violation" error.
 *
 * Historical note: pre-migration the chat client enforced OpenAI-specific
 * PDF-only caps (10 MB / file, 32 MB total). Those were leaking a
 * provider-specific constraint into every chat upload regardless of target
 * model, and didn't cover any other file type. This module replaces that
 * with a single per-file ceiling aligned to the actual server cap, plus a
 * chat-only count cap.
 */

/**
 * Per-file byte ceiling enforced on both chat attachments and workspace
 * uploads. Must stay in sync with the backend's S3 PresignPostObject
 * `content-length-range` condition.
 */
export const FILE_RECORD_MAX_SIZE = 100 * 1024 * 1024; // 100 MB

/**
 * Maximum number of attachments allowed on a single chat message. Not
 * enforced on workspace uploads — a workspace is a persistent asset
 * collection, batch-uploading 50 files is a reasonable UX. Enforced on
 * chat because LLM providers cap per-turn context size and 5 is a
 * sensible UX ceiling.
 */
export const MAX_FILES_PER_MESSAGE = 5;

function formatMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

/**
 * Returns true when a file is over the shared per-file cap. Used both as a
 * gate and to drive inline "too large" UI affordances.
 */
export function exceedsFileRecordMaxSize(file: Pick<File, "size">): boolean {
  return file.size > FILE_RECORD_MAX_SIZE;
}

export type FileRecordValidationErrorType = "FILE_TOO_LARGE" | "TOO_MANY_FILES";

export interface FileRecordValidationResult {
  isValid: boolean;
  error?: string;
  errorType?: FileRecordValidationErrorType;
}

/**
 * Validate a batch about to be attached to a chat message. Applies both the
 * per-file size cap and the per-message count cap. Returns the first violation
 * encountered with a user-presentable error string.
 *
 * Skips entries without a `file` payload (e.g. already-uploaded attachments
 * being re-validated as the send-button gate rechecks) — those bypass the
 * size check because we don't have the bytes to measure.
 */
export function validateChatMessageFiles(
  files: Array<{ file?: File; name: string }>,
): FileRecordValidationResult {
  if (files.length > MAX_FILES_PER_MESSAGE) {
    return {
      isValid: false,
      error: `Too many files attached (${files.length}). Maximum ${MAX_FILES_PER_MESSAGE} files per message.`,
      errorType: "TOO_MANY_FILES",
    };
  }

  for (const uploaded of files) {
    const file = uploaded.file;
    if (!file) continue;
    if (exceedsFileRecordMaxSize(file)) {
      return {
        isValid: false,
        error: `File "${uploaded.name}" is ${formatMB(file.size)}. Files must be ${formatMB(FILE_RECORD_MAX_SIZE)} or smaller.`,
        errorType: "FILE_TOO_LARGE",
      };
    }
  }

  return { isValid: true };
}

/**
 * Split a batch of pending uploads into the ones that pass the per-file size
 * cap and the ones that don't. Used by the workspace upload queue: we want
 * to upload the acceptable files and surface a toast for each rejected one
 * instead of aborting the whole drop (workspace users routinely drop mixed
 * batches — one 200 MB rogue file shouldn't kill the other 20).
 */
export function partitionFilesBySizeLimit(files: File[]): {
  accepted: File[];
  rejected: File[];
} {
  const accepted: File[] = [];
  const rejected: File[] = [];
  for (const file of files) {
    if (exceedsFileRecordMaxSize(file)) {
      rejected.push(file);
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejected };
}

/** Build a "File too large" toast message for a rejected File. */
export function describeOversizedFile(file: File): string {
  return `"${file.name}" is ${formatMB(file.size)}. Files must be ${formatMB(FILE_RECORD_MAX_SIZE)} or smaller.`;
}
